<template>
  <div v-if="!!phaseData">
    <field-label v-if="!attendance.data.summarizedFlow" label="Quais sintomas tem ou já teve?">
      <v-switch
        class="mb-8 width-fit-content"
        label="Não possui sintomas"
        @change="onNoSymptomsChange"
        v-model="phaseData.noSymptoms"
        hide-details
      />
    </field-label>

    <field-label v-if="attendance.data.summarizedFlow" label="Há quanto tempo o paciente tem sintomas?">
      <div class="d-flex mt-2" :class="{ 'align-center': $vuetify.breakpoint.smAndDown }">
        <text-field class="ml-4" :mask="['##']" hide-details v-model="phaseData.symptomsTime.period.length" />
        <radio-options
          v-model="phaseData.symptomsTime.period.type"
          class="ml-8 mt-0"
          :options="defaultPeriodOptions"
          row
          hide-details
        />
      </div>
    </field-label>

    <field-label label="Sintomas" class="mt-4">
      <template v-if="!phaseData.noSymptoms || attendance.data.summarizedFlow">
        <div v-for="(symptomOption, index) in symptomOptions" :key="`symptom-${symptomOption.key}`">
          <v-divider v-if="index !== 0" class="my-4" />
          <v-sheet class="d-flex" min-height="40px" :class="{ 'flex-wrap': $vuetify.breakpoint.smAndDown }">
            <div>
              <v-checkbox
                class="mt-0"
                :label="symptomOption.name"
                hide-details
                v-model="phaseData.symptoms[symptomOption.key].value"
                @change="onSymptomChange(symptomOption.key, $event)"
              />
              <small>{{ symptomOption.subtitle }}</small>
            </div>
            <info-btn v-if="!!symptomOption.imageUrl" @click="showImage(symptomOption.imageUrl, symptomOption.name)" />
            <template v-if="phaseData.symptoms[symptomOption.key].value && !attendance.data.summarizedFlow">
              <div class="d-flex" :class="{ 'align-center': $vuetify.breakpoint.smAndDown }">
                <text-field
                  class="ml-4"
                  :mask="['##']"
                  v-model="phaseData.symptoms[symptomOption.key].period.length"
                  hide-details
                />
                <radio-options
                  class="ml-8 mt-0"
                  :options="defaultPeriodOptions"
                  v-model="phaseData.symptoms[symptomOption.key].period.type"
                  row
                  hide-details
                />
              </div>
            </template>
          </v-sheet>
          <v-sheet v-if="phaseData.symptoms[symptomOption.key].value && symptomOption.options" class="px-6">
            <div class="d-flex" v-for="option in symptomOption.options" :key="`option-${option.key}`">
              <v-checkbox
                class="mt-0"
                :label="option.name"
                dense
                multiple
                :value="option.key"
                v-model="phaseData.symptoms[symptomOption.key].options"
                hide-details
              />
              <info-btn v-if="!!option.imageUrl" @click="showImage(option.imageUrl, symptomOption.name, option.name)" />

              <text-field
                v-if="showOtherTextFieldForPsoriasis(symptomOption, option)"
                class="ml-2 flex-grow-1"
                :key="`other-text-${option.key}`"
                v-model="phaseData.psoriaseOtherSymptom"
                :max-length="500"
              />
            </div>
          </v-sheet>
        </div>
        <div class="mt-4">
          <div class="d-flex gap-2 align-center" :class="{ 'flex-wrap': $vuetify.breakpoint.smAndDown }">
            <search-server-field
              label="Adição de outros sintomas"
              class="flex-grow-1"
              path="cid-symptom"
              v-model="otherSymptom.value"
              hide-details
            />
            <div class="d-flex align-center gap-4" v-if="!attendance.data.summarizedFlow">
              <text-field label="Duração" :mask="['##']" v-model="otherSymptom.period.length" hide-details />
              <radio-options
                class="mt-0"
                :options="defaultPeriodOptions"
                v-model="otherSymptom.period.type"
                row
                hide-details
              />
            </div>
            <v-btn class="mt-1" outlined color="primary" small @click="addSymptom" :disabled="invalidOtherSymptomData"
              >adicionar <v-icon small class="ml-2">mdi-plus-circle-outline</v-icon></v-btn
            >
          </div>
          <ul class="mt-2">
            <li
              v-for="(otherSymptom, index) in phaseData.otherSymptoms"
              :key="`other-symptom-${otherSymptom.type}-${otherSymptom.id}`"
            >
              {{ otherSymptom.name }} - {{ otherSymptom.period.length }}
              {{
                otherSymptom.period.type === 'MES'
                  ? otherSymptom.period.length === 1
                    ? 'mês'
                    : 'meses'
                  : otherSymptom.period.length === 1
                  ? 'ano'
                  : 'anos'
              }}
              <v-btn icon small color="error" @click="removeSymptom(index)"><v-icon small>mdi-close</v-icon></v-btn>
            </li>
          </ul>
        </div>

        <template v-if="attendance.data.summarizedFlow">
          <radio-options
            class="mt-8"
            label="O paciente tem diagnóstico de espondiloartrite?"
            :options="diagnosisSpondyloarthritisOptions"
            v-model="phaseData.diagnosisSpondyloarthritis.value"
            clearable
            row
            hide-details
          />
          <template v-if="[undefined, null, true].indexOf(phaseData.diagnosisSpondyloarthritis.value) > -1">
            <div class="d-flex mt-2" :class="{ 'align-center': $vuetify.breakpoint.smAndDown }">
              <text-field
                class="ml-4"
                :mask="['##']"
                hide-details
                v-model="phaseData.diagnosisSpondyloarthritis.period.length"
              />
              <radio-options
                v-model="phaseData.diagnosisSpondyloarthritis.period.type"
                class="ml-8 mt-0"
                :options="defaultPeriodOptions"
                row
                hide-details
              />
            </div>
            <text-field
              label="Qual especialidade fez o diagnóstico?"
              class="mt-8"
              hide-details
              v-model="phaseData.diagnosisSpondyloarthritis.specialist"
            />
          </template>
        </template>

        <radio-options
          class="mt-8"
          label="Qual o sintoma predominante?"
          :options="predominantSymptomOptions"
          v-model="phaseData.predominantSymptom"
          :disabled="!!computedPredominantSymptom"
          clearable
          row
          hide-details
        />
      </template>

      <field-label class="mt-8" label="Anotações">
        <text-area :readonly="disabled" :max-length="2000" v-model="phaseData.notes" />
      </field-label>
    </field-label>
    <reference-image-dialog ref="referenceImageDialog" />
  </div>
</template>

<script>
import { createNamespacedHelpers } from 'vuex'
import FieldLabel from '@/components/FieldLabel.vue'
import TextField from '@/components/TextField.vue'
import ReferenceImageDialog from '../../ReferenceImageDialog.vue'
import InfoBtn from '@/components/InfoBtn.vue'
import TextArea from '@/components/TextArea.vue'
import SearchServerField from '@/components/SearchServerField.vue'
import RadioOptions from '@/components/RadioOptions.vue'
import BasePhase from '@/components/AttendanceForm/general/phase/BasePhase.vue'
import { SYMPTOM_OPTIONS } from '@/components/AttendanceForm/espondiloartrites/constants/symptom'
const { mapGetters: attendanceGettersMap } = createNamespacedHelpers('attendance')

export default {
  extends: BasePhase,
  components: { FieldLabel, TextField, ReferenceImageDialog, InfoBtn, TextArea, SearchServerField, RadioOptions },
  computed: {
    ...attendanceGettersMap(['attendance']),

    invalidOtherSymptomData() {
      return !this.otherSymptom.value || !parseInt(this.otherSymptom.period.length)
    },
    computedPredominantSymptom() {
      if (!this.phaseData?.symptoms) {
        return null
      }

      const chosenSymptomsTypes = Object.entries(this.phaseData.symptoms)
        .filter((entry) => entry[1].value)
        .map((entry) => this.symptomOptions.find((item) => item.key === entry[0]).type)

      if (this.phaseData.otherSymptoms.length > 0) {
        chosenSymptomsTypes.push('PERIFERICO')
      }

      const uniqueSymptomTypes = [...new Set(chosenSymptomsTypes)]

      return uniqueSymptomTypes.length === 1 ? uniqueSymptomTypes[0] : null
    },
    asasPhaseData() {
      return this.getPhaseDataByKey('asas')
    },
    personalHistoryPhaseData() {
      return this.getPhaseDataByKey('investigacao_pessoal')
    },
    predominantSymptom() {
      return this.phaseData?.predominantSymptom
    },
    defaultPeriodOptions() {
      return [
        { text: 'Meses', value: 'MES' },
        { text: 'Anos', value: 'ANO' },
      ]
    },
    predominantSymptomOptions() {
      return [
        { text: 'Axial', value: 'AXIAL' },
        { text: 'Periférico', value: 'PERIFERICO' },
      ]
    },
    diagnosisSpondyloarthritisOptions() {
      return [
        { text: 'Sim', value: true },
        { text: 'Não', value: false },
        { text: 'Em investicação', value: 'EM INVESTIGAÇÃO' },
      ]
    },
  },
  watch: {
    computedPredominantSymptom(newVal) {
      this.phaseData.predominantSymptom = newVal
    },
  },
  data() {
    return {
      otherSymptom: {
        value: null,
        period: {
          length: null,
          type: 'MES',
        },
      },
      symptomOptions: SYMPTOM_OPTIONS,
    }
  },
  methods: {
    getDefaultData() {
      const data = {
        psoriaseOtherSymptom: null,
        predominantSymptom: null,
        notes: null,
        noSymptoms: false,
        otherSymptoms: [],
        symptoms: this.buildEmptySymptomsObject(),
        symptomsTime: {
          period: {
            type: null,
            length: null,
          },
        },
        diagnosisSpondyloarthritis: {
          value: null,
          period: {
            type: null,
            length: null,
          },
          physicianSpecialty: null,
        },
      }

      return data
    },
    buildEmptySymptomsObject() {
      return this.symptomOptions.reduce(
        (acc, option) => ({
          ...acc,
          [option.key]: { value: false, period: { length: null, type: 'MES' }, options: [] },
        }),
        {},
      )
    },
    showImage(imageSrc, symptomName, symptomOptionName = null) {
      this.$refs.referenceImageDialog.openDialog(
        imageSrc,
        'Imagem de exemplo de sintoma',
        symptomOptionName ? `${symptomName} - ${symptomOptionName}` : `${symptomName}`,
      )
    },
    clearRelatedDataIfSymptomIsNotChecked(key, value) {
      this.phaseData.symptoms[key].options = []
      this.phaseData.symptoms[key].period.length = null
      this.phaseData.symptoms[key].period.type = 'MES'

      if (key === 'psoriase_outros') {
        this.phaseData.psoriaseOtherSymptom = null
      }
    },
    onSymptomChange(key, value) {
      if (!!this.asasPhaseData) {
        if (key === 'dactilite') {
          this.asasPhaseData.caspar_dactilite = value
        }
        if (key === 'alteracao_habito_intestinal') {
          if (this.predominantSymptom === 'AXIAL') {
            this.asasPhaseData.doenca_de_crohn_ou_retocolite_ulcerativa = value
          }
          if (this.predominantSymptom === 'PERIFERICO') {
            this.asasPhaseData.doenca_inflamatoria_intestinal = value
          }
        }
      }
      if (!!this.personalHistoryPhaseData) {
        if (key === 'alteracao_habito_intestinal') {
          this.personalHistoryPhaseData.profile.diarreia_cronica.value = value

          if (value) {
            this.personalHistoryPhaseData.diseases.push('doenca_inflamatoria_intestinal')
          } else {
            this.personalHistoryPhaseData.diseases = this.personalHistoryPhaseData.diseases.filter(
              (disease) => disease !== 'doenca_inflamatoria_intestinal',
            )
          }
        }
        if (key === 'uveite') {
          this.personalHistoryPhaseData.profile.uveite.value = value
        }
      }
      if (!value) {
        this.clearRelatedDataIfSymptomIsNotChecked(key, value)
      }
    },
    onNoSymptomsChange(value) {
      this.phaseData.symptoms = value ? null : this.buildEmptySymptomsObject()
      this.phaseData.predominantSymptom = null
    },
    showOtherTextFieldForPsoriasis(symptomOption, option) {
      const otherOptionKey = 'outros'
      return (
        option.key === otherOptionKey && this.phaseData.symptoms[symptomOption.key].options.includes(otherOptionKey)
      )
    },
    addSymptom() {
      this.phaseData.otherSymptoms.push({
        id: this.otherSymptom.value.id,
        name: this.otherSymptom.value.name,
        type: this.otherSymptom.value.type,
        period: { ...this.otherSymptom.period },
      })

      this.otherSymptom.value = null
      this.otherSymptom.period.length = null
      this.otherSymptom.period.type = 'MES'
    },
    removeSymptom(index) {
      this.phaseData.otherSymptoms.splice(index, 1)
    },
  },
  mounted() {
    if (!this.phaseData.symptomsTime) {
      this.phaseData['symptomsTime'] = {
        period: {
          type: null,
          length: null,
        },
      }
    }
    if (!this.phaseData.diagnosisSpondyloarthritis) {
      this.phaseData['diagnosisSpondyloarthritis'] = {
        value: null,
        period: {
          type: null,
          length: null,
        },
        physicianSpecialty: null,
      }
    }
  },
}
</script>

<style></style>
